import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { LogOut, User, LayoutDashboard, ChevronDown, Users, ArrowLeft, Shield, Sparkles } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import logoBranca from '@/assets/logo-branca.webp';

const CONSULTANT_SESSION_KEY = 'consultant_original_session';
const IMPERSONATING_KEY = 'consultant_impersonating';
const sessionStore = sessionStorage;

interface ClientOption {
  client_user_id: string;
  client_name: string;
  has_completed_test?: boolean;
}

export function GlobalHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, signOut } = useAuth();
  const [userName, setUserName] = useState<string | null>(null);
  const [isConsultant, setIsConsultant] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [clientName, setClientName] = useState<string | null>(null);
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Fetch user profile and role
  useEffect(() => {
    if (!user) {
      setUserName(null);
      setIsConsultant(false);
      return;
    }

    const fetchProfile = async () => {
      const { data: profile } = await supabase
        .from('profiles')
        .select('nome')
        .eq('id', user.id)
        .maybeSingle();
      setUserName(profile?.nome || user.email?.split('@')[0] || 'Usuário');

      const { data: roles } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id);
      setIsConsultant(roles?.some(r => r.role === 'consultant') || false);
      setIsAdmin(roles?.some(r => r.role === 'admin') || false);
    };

    fetchProfile();
  }, [user]);

  // Watch impersonation state
  const checkImpersonation = useCallback(() => {
    const stored = sessionStore.getItem(IMPERSONATING_KEY);
    if (stored) {
      try {
        const data = JSON.parse(stored);
        setIsImpersonating(true);
        setClientName(data.clientName);
      } catch {
        setIsImpersonating(false);
        setClientName(null);
      }
    } else {
      setIsImpersonating(false);
      setClientName(null);
    }
  }, []);

  useEffect(() => {
    checkImpersonation();
    window.addEventListener('impersonation-changed', checkImpersonation);
    return () => window.removeEventListener('impersonation-changed', checkImpersonation);
  }, [checkImpersonation]);

  // Fetch clients when impersonating
  const fetchClients = useCallback(async () => {
    const stored = sessionStore.getItem(CONSULTANT_SESSION_KEY);
    if (!stored) return;
    try {
      const { access_token } = JSON.parse(stored);
      const { data, error } = await supabase.functions.invoke('manage-consultant-clients', {
        body: { action: 'list-clients' },
        headers: { Authorization: `Bearer ${access_token}` },
      });
      if (!error && data?.clients) setClients(data.clients);
    } catch {}
  }, []);

  useEffect(() => {
    if (isImpersonating) fetchClients();
  }, [isImpersonating, fetchClients]);

  const handleStopImpersonating = async () => {
    try {
      const stored = sessionStore.getItem(CONSULTANT_SESSION_KEY);
      if (stored) {
        const { access_token, refresh_token } = JSON.parse(stored);
        await supabase.auth.setSession({ access_token, refresh_token });
      }
      sessionStore.removeItem(CONSULTANT_SESSION_KEY);
      sessionStore.removeItem(IMPERSONATING_KEY);
      setIsImpersonating(false);
      setClientName(null);
      window.dispatchEvent(new Event('impersonation-changed'));
      navigate('/consultor');
    } catch {
      sessionStore.removeItem(CONSULTANT_SESSION_KEY);
      sessionStore.removeItem(IMPERSONATING_KEY);
      window.dispatchEvent(new Event('impersonation-changed'));
      navigate('/consultor');
    }
  };

  const handleSwitchClient = async (clientUserId: string, newClientName: string, hasCompletedTest: boolean) => {
    if (newClientName === clientName) return;
    try {
      const stored = sessionStore.getItem(CONSULTANT_SESSION_KEY);
      if (!stored) return;
      const { access_token, refresh_token } = JSON.parse(stored);
      await supabase.auth.setSession({ access_token, refresh_token });

      const { data, error } = await supabase.functions.invoke('manage-consultant-clients', {
        body: { action: 'impersonate-client', clientUserId },
      });
      if (error || data?.error) throw new Error(data?.error || 'Erro ao trocar subconta');

      sessionStore.setItem(CONSULTANT_SESSION_KEY, JSON.stringify({ access_token, refresh_token }));
      await supabase.auth.setSession({
        access_token: data.session.access_token,
        refresh_token: data.session.refresh_token,
      });
      sessionStore.setItem(IMPERSONATING_KEY, JSON.stringify({ clientName: newClientName, clientUserId }));
      setClientName(newClientName);
      setDropdownOpen(false);
      window.dispatchEvent(new Event('impersonation-changed'));
      navigate(hasCompletedTest ? '/app' : '/teste');
    } catch (err) {
      console.error('Switch client error:', err);
    }
  };

  const handleLogout = async () => {
    sessionStore.removeItem(CONSULTANT_SESSION_KEY);
    sessionStore.removeItem(IMPERSONATING_KEY);
    await signOut();
    navigate('/');
  };

  if (loading || !user) return null;

  return (
    <header className="fixed top-0 left-0 right-0 z-[60] bg-background/80 backdrop-blur-md border-b border-border/30">
      {/* Impersonation banner */}
      {isImpersonating && clientName && (
        <div className="bg-primary text-primary-foreground px-3 sm:px-4 py-1.5 pt-[calc(0.375rem+env(safe-area-inset-top,0px))]">
          <div className="container mx-auto flex flex-wrap items-center justify-between gap-2">
            <div className="relative min-w-0">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex max-w-full items-center gap-2 bg-primary-foreground/15 hover:bg-primary-foreground/25 transition-colors px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium"
              >
                <User className="w-3.5 h-3.5 shrink-0" />
                <span className="truncate">Operando com: <strong>{clientName}</strong></span>
                <ChevronDown className={`w-3.5 h-3.5 shrink-0 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {dropdownOpen && clients.length > 0 && (
                <div className="absolute top-full left-0 mt-2 w-[min(16rem,calc(100vw-2rem))] bg-background border border-border rounded-lg shadow-xl overflow-hidden z-[70]">
                  <div className="px-3 py-2 border-b border-border/50">
                    <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Trocar subconta</span>
                  </div>
                  <div className="max-h-60 overflow-y-auto">
                    {clients.map((c) => (
                      <button
                        key={c.client_user_id}
                        onClick={() => handleSwitchClient(c.client_user_id, c.client_name, !!c.has_completed_test)}
                        className={`w-full text-left px-3 py-2.5 text-sm flex items-center gap-2 hover:bg-accent/10 transition-colors text-foreground ${
                          c.client_name === clientName ? 'bg-accent/5 font-semibold' : ''
                        }`}
                      >
                        <Users className="w-4 h-4 text-muted-foreground shrink-0" />
                        <span className="truncate">{c.client_name}</span>
                        {!c.has_completed_test && (
                          <span className="ml-auto text-[10px] bg-accent/20 text-accent px-1.5 py-0.5 rounded-full shrink-0">Sem teste</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={handleStopImpersonating}
              className="flex shrink-0 items-center gap-1 text-xs sm:text-sm font-medium hover:opacity-80 transition-opacity bg-primary-foreground/20 px-3 py-1.5 rounded-full"
            >
              <ArrowLeft className="w-4 h-4 shrink-0" />
              <span className="whitespace-nowrap">Voltar ao Painel</span>
            </button>
          </div>
        </div>
      )}

      {/* Main header bar (oculto no mobile, visível em md+) */}
      <div className="container mx-auto px-4 py-3 hidden md:flex items-center justify-between">
        <Link to="/" className="flex flex-col items-center">
          <img src={logoBranca} alt="12 Personas de Estilo" className="h-7 md:h-9 w-auto" />
        </Link>

        <div className="flex items-center gap-4">
          <span className="text-sm text-muted-foreground truncate max-w-[120px] sm:max-w-none">{userName}</span>

          {!isConsultant && location.pathname !== '/app' && (
            <Link
              to="/app"
              className="text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 transition-colors flex items-center gap-1.5 px-3 py-1.5 rounded-full"
            >
              <Sparkles className="w-4 h-4" />
              <span>Ir ao App</span>
            </Link>
          )}

          {isAdmin && (
            <Link
              to="/admin"
              className="text-sm text-primary hover:text-primary/80 transition-colors flex items-center gap-1"
            >
              <Shield className="w-4 h-4" />
              <span className="hidden sm:inline">Admin</span>
            </Link>
          )}

          {isConsultant && !isImpersonating && (
            <Link
              to="/consultor"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
            >
              <LayoutDashboard className="w-4 h-4" />
              <span className="hidden sm:inline">Painel</span>
            </Link>
          )}

          {!isConsultant && (
            <Link
              to="/resultado"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
            >
              <User className="w-4 h-4" />
              <span className="hidden sm:inline">Perfil</span>
            </Link>
          )}

          <button
            onClick={handleLogout}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
          >
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline">Sair</span>
          </button>
        </div>
      </div>
    </header>
  );
}